import Swiper from "./swiper-bundle.esm.browser.min.js";

const MOBILE_BREAKPOINT = 769;

class OurMission extends HTMLElement {
  constructor() {
    super();
    this._swiper = null;
    this._resizeTimer = null;
    this._initialized = false;
    this._paginationHandlers = [];
    this._readMoreHandler = null;
    this._handleResize = this._handleResize.bind(this);
    this._handleSectionLoad = this._handleSectionLoad.bind(this);
    this._handleSectionUnload = this._handleSectionUnload.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    document.addEventListener("shopify:section:load", this._handleSectionLoad);
    document.addEventListener("shopify:section:unload", this._handleSectionUnload);
    window.addEventListener("resize", this._handleResize, { passive: true });
    this._initReadMore();
    this._updateLayout();
  }

  disconnectedCallback() {
    this._initialized = false;
    this._destroySwiper();
    this._removePaginationHandlers();
    this._teardownReadMore();
    window.removeEventListener("resize", this._handleResize);
    document.removeEventListener("shopify:section:load", this._handleSectionLoad);
    document.removeEventListener("shopify:section:unload", this._handleSectionUnload);
    if (this._resizeTimer) {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = null;
    }
  }

  _isOurSection(event) {
    const section = this.closest(".shopify-section");
    return section && (event.target === section || section.contains(event.target));
  }

  _handleSectionLoad(event) {
    if (!this._isOurSection(event)) return;
    this._destroySwiper();
    this._initReadMore();
    this._updateLayout();
  }

  _handleSectionUnload(event) {
    if (!this._isOurSection(event)) return;
    this._destroySwiper();
    this._teardownReadMore();
  }

  _readConfig() {
    const slideCount = parseInt(this.dataset.slideCount || "0", 10);
    return {
      slideCount,
      sectionId: this.dataset.sectionId || "",
      spaceBetween: parseInt(this.dataset.spaceBetween || "20", 10),
      mobileSlidesPerView: parseFloat(this.dataset.slidesPerViewMobile || "1.2"),
      showArrows: this.dataset.showArrows === "true",
      showPagination: this.dataset.showPagination === "true",
      enableLoop: this.dataset.enableLoop === "true",
      enableAutoplay: this.dataset.enableAutoplay === "true",
      autoplayDelay: parseInt(this.dataset.autoplayDelay || "5", 10) * 1000,
      pauseOnHover: this.dataset.pauseOnHover !== "false",
      pauseOnInteraction: this.dataset.pauseOnInteraction === "true",
      descriptionMaxLength: parseInt(this.dataset.descriptionMaxLength || "200", 10),
      descriptionMaxLines: parseInt(this.dataset.descriptionMaxLines || "4", 10),
    };
  }

  _isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  _shouldUseSwiper(config) {
    return config.slideCount > 1;
  }

  _cleanupSwiperClasses() {
    const swiperEl = this.querySelector("[data-swiper]");
    const container = this.querySelector("[data-swiper-container]");
    const slides = this.querySelectorAll("[data-swiper-slide]");

    if (swiperEl) {
      swiperEl.classList.remove("swiper", "om__swiper", "swiper-initialized");
    }
    if (container) {
      container.classList.remove("swiper-wrapper");
    }
    slides.forEach((slide) => slide.classList.remove("swiper-slide"));
  }

  _applyStaticMode() {
    this.classList.add("om--static");
    this.classList.remove("om--swiper");
    this._cleanupSwiperClasses();
    this._syncSlideAccessibility(0);
    this._syncPaginationState(0);
  }

  _prepareSwiperStructure() {
    const swiperEl = this.querySelector("[data-swiper]");
    const container = this.querySelector("[data-swiper-container]");
    const slideEls = this.querySelectorAll("[data-swiper-slide]");

    if (!swiperEl || !container || slideEls.length === 0) return null;

    swiperEl.classList.add("swiper", "om__swiper");
    container.classList.add("swiper-wrapper");
    slideEls.forEach((slide) => slide.classList.add("swiper-slide"));

    return { slideEls };
  }

  _syncSlideAccessibility(activeIndex) {
    const slides = this.querySelectorAll("[data-swiper-slide]");
    slides.forEach((slide, index) => {
      const isActive = index === activeIndex;
      slide.setAttribute("aria-hidden", isActive ? "false" : "true");
    });
  }

  _syncPaginationState(activeIndex) {
    const buttons = this.querySelectorAll(".om__pagination-btn[data-slide-index]");
    buttons.forEach((button) => {
      const index = parseInt(button.dataset.slideIndex || "0", 10);
      const isActive = index === activeIndex;
      button.classList.toggle("om__pagination-btn--active", isActive);
      button.setAttribute("aria-selected", isActive ? "true" : "false");
      if (isActive) {
        button.setAttribute("aria-current", "true");
      } else {
        button.removeAttribute("aria-current");
      }
    });
  }

  _removePaginationHandlers() {
    this._paginationHandlers.forEach(({ button, handler }) => {
      button.removeEventListener("click", handler);
    });
    this._paginationHandlers = [];
  }

  _bindPagination(config) {
    this._removePaginationHandlers();
    if (!config.showPagination) return;

    const buttons = this.querySelectorAll(".om__pagination-btn[data-slide-index]");
    buttons.forEach((button) => {
      const handler = () => {
        const index = parseInt(button.dataset.slideIndex || "0", 10);
        if (!this._swiper || this._swiper.destroyed) {
          this._syncSlideAccessibility(index);
          this._syncPaginationState(index);
          return;
        }

        if (this._swiper.params.loop) {
          this._swiper.slideToLoop(index);
        } else {
          this._swiper.slideTo(index);
        }
      };

      button.addEventListener("click", handler);
      this._paginationHandlers.push({ button, handler });
    });
  }

  _initSwiper(config) {
    const structure = this._prepareSwiperStructure();
    if (!structure) return;

    const { slideEls } = structure;
    const isMobile = this._isMobile();
    const slidesPerView = isMobile ? config.mobileSlidesPerView : 1;
    const canNavigate = slideEls.length > 1;
    const canLoop =
      config.enableLoop && canNavigate && slideEls.length > slidesPerView;

    this.classList.add("om--swiper");
    this.classList.remove("om--static");

    const swiperConfig = {
      slidesPerView,
      spaceBetween: config.spaceBetween,
      watchOverflow: true,
      grabCursor: canNavigate,
      speed: 350,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      a11y: {
        enabled: true,
        prevSlideMessage: "Previous slide",
        nextSlideMessage: "Next slide",
      },
    };

    if (canLoop) {
      swiperConfig.loop = true;
    }

    if (config.enableAutoplay && canNavigate) {
      swiperConfig.autoplay = {
        delay: config.autoplayDelay,
        disableOnInteraction: config.pauseOnInteraction,
        pauseOnMouseEnter: config.pauseOnHover,
      };
    }

    if (config.showArrows && canNavigate) {
      const nextEl = this.querySelector(`.om__nav-next--${config.sectionId}`);
      const prevEl = this.querySelector(`.om__nav-prev--${config.sectionId}`);
      if (nextEl && prevEl) {
        swiperConfig.navigation = { nextEl, prevEl };
      }
    }

    const existingOn = swiperConfig.on || {};
    swiperConfig.on = {
      ...existingOn,
      afterInit: (swiper) => {
        existingOn.afterInit?.(swiper);
        const activeIndex = swiper.realIndex ?? swiper.activeIndex;
        this._syncSlideAccessibility(activeIndex);
        this._syncPaginationState(activeIndex);
        this._bindMissionToolbarLayout();
      },
      slideChangeTransitionEnd: (swiper) => {
        existingOn.slideChangeTransitionEnd?.(swiper);
        const activeIndex = swiper.realIndex ?? swiper.activeIndex;
        this._syncSlideAccessibility(activeIndex);
        this._syncPaginationState(activeIndex);
      },
      resize: (swiper) => {
        existingOn.resize?.(swiper);
        swiper.params.slidesPerView = this._isMobile()
          ? config.mobileSlidesPerView
          : 1;
        swiper.update();
        this._bindMissionToolbarLayout();
      },
    };

    const swiperEl = this.querySelector("[data-swiper]");
    this._swiper = new Swiper(swiperEl, swiperConfig);
    this._bindPagination(config);
    requestAnimationFrame(() => {
      this._swiper?.update();
      this._bindMissionToolbarLayout();
    });
  }

  _clearMissionToolbarLayout() {
    const swiperEl = this.querySelector("[data-swiper]");
    const toolbar = this.querySelector(".om__toolbar");

    swiperEl?.style.removeProperty("min-height");
    toolbar?.style.removeProperty("--om-toolbar-top");

    this.querySelectorAll("[data-swiper-slide]").forEach((slide) => {
      slide.style.removeProperty("min-height");
      slide.querySelector(".om__media")?.style.removeProperty("min-height");
      slide.querySelector(".om__card-content")?.style.removeProperty("min-height");
    });
  }

  _measureMissionMobileHeights() {
    const slider = this.querySelector(".om__slider");
    const slides = this.querySelectorAll("[data-swiper-slide]");
    const containerWidth = slider?.clientWidth || window.innerWidth;
    let maxMedia = 0;
    let maxContent = 0;

    slides.forEach((slide) => {
      const cardInner = slide.querySelector(".om__card-inner");
      const content = slide.querySelector(".om__card-content");

      if (content && cardInner) {
        const clone = content.cloneNode(true);
        clone.style.cssText =
          "position:absolute;visibility:hidden;pointer-events:none;width:100%;left:0;top:0;";
        cardInner.appendChild(clone);
        maxContent = Math.max(maxContent, clone.offsetHeight);
        clone.remove();
      }

      const img = slide.querySelector(".om__image:not(.om__image--placeholder)");
      if (img?.naturalWidth > 0) {
        maxMedia = Math.max(
          maxMedia,
          containerWidth * (img.naturalHeight / img.naturalWidth)
        );
      } else {
        const media = slide.querySelector(".om__media");
        if (media) {
          maxMedia = Math.max(maxMedia, media.getBoundingClientRect().height);
        }
      }
    });

    const spacerHeight = 44;
    return {
      maxMedia: Math.ceil(maxMedia),
      maxContent: Math.ceil(maxContent),
      maxSlide: Math.ceil(maxMedia) + spacerHeight + Math.ceil(maxContent),
    };
  }

  _applyMissionToolbarLayout() {
    if (window.innerWidth >= 769) {
      this._clearMissionToolbarLayout();
      return;
    }

    const swiperEl = this.querySelector("[data-swiper]");
    const toolbar = this.querySelector(".om__toolbar");
    const slides = this.querySelectorAll("[data-swiper-slide]");
    if (!swiperEl || !slides.length) return;

    const { maxMedia, maxContent, maxSlide } = this._measureMissionMobileHeights();
    if (!maxSlide) return;

    slides.forEach((slide) => {
      const media = slide.querySelector(".om__media");
      const content = slide.querySelector(".om__card-content");
      if (media && maxMedia) media.style.minHeight = `${maxMedia}px`;
      if (content && maxContent) content.style.minHeight = `${maxContent}px`;
      slide.style.minHeight = `${maxSlide}px`;
    });

    swiperEl.style.minHeight = `${maxSlide}px`;
    toolbar?.style.setProperty("--om-toolbar-top", `${maxMedia}px`);
  }

  _bindMissionToolbarLayout() {
    const runLayout = () => {
      requestAnimationFrame(() => this._applyMissionToolbarLayout());
    };

    runLayout();

    const images = this.querySelectorAll(".om__image:not(.om__image--placeholder)");
    images.forEach((img) => {
      if (img.complete) return;
      img.addEventListener("load", runLayout, { once: true });
    });
  }

  _destroySwiper() {
    this._removePaginationHandlers();
    this._clearMissionToolbarLayout();
    if (this._swiper && !this._swiper.destroyed) {
      this._swiper.destroy(true, true);
    }
    this._swiper = null;
    this._cleanupSwiperClasses();
  }

  _updateLayout() {
    const config = this._readConfig();
    const shouldUseSwiper = this._shouldUseSwiper(config);
    const isSwiperActive = Boolean(this._swiper);

    if (!shouldUseSwiper) {
      this._destroySwiper();
      this._applyStaticMode();
      this._bindPagination(config);
      this._bindMissionToolbarLayout();
      return;
    }

    if (isSwiperActive) {
      this._destroySwiper();
    }

    this._initSwiper(config);
  }

  _handleResize() {
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => {
      this._updateReadMore();
      const config = this._readConfig();
      const shouldUseSwiper = this._shouldUseSwiper(config);
      const isSwiperActive = Boolean(this._swiper);

      if (shouldUseSwiper !== isSwiperActive) {
        this._updateLayout();
        return;
      }

      if (this._swiper && !this._swiper.destroyed) {
        this._swiper.params.slidesPerView = this._isMobile()
          ? config.mobileSlidesPerView
          : 1;
        this._swiper.update();
      }

      this._bindMissionToolbarLayout();
    }, 150);
  }

  _contentOverflows(content, lines) {
    if (!content || !content.parentElement) return false;

    const width = content.offsetWidth;
    if (!width) return false;

    const full = content.cloneNode(true);
    full.removeAttribute("id");
    full.setAttribute("aria-hidden", "true");
    full.classList.remove("om__description--collapsed");
    full.style.cssText =
      "position:absolute;visibility:hidden;pointer-events:none;left:0;top:0;width:" +
      width +
      "px;display:block;-webkit-line-clamp:unset;max-height:none;overflow:visible;height:auto;";

    const clamped = content.cloneNode(true);
    clamped.removeAttribute("id");
    clamped.setAttribute("aria-hidden", "true");
    clamped.classList.add("om__description--collapsed");
    clamped.style.cssText =
      "position:absolute;visibility:hidden;pointer-events:none;left:0;top:0;width:" +
      width +
      "px;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:" +
      lines +
      ";overflow:hidden;height:auto;max-height:none;";

    content.parentElement.appendChild(full);
    content.parentElement.appendChild(clamped);
    const overflows = full.scrollHeight > clamped.clientHeight + 1;
    content.parentElement.removeChild(full);
    content.parentElement.removeChild(clamped);

    return overflows;
  }

  _setReadMoreExpanded(wrap, expanded) {
    const content = wrap.querySelector("[data-om-read-more-content]");
    const toggle = wrap.querySelector("[data-om-read-more-toggle]");
    const moreLabel = wrap.querySelector("[data-om-read-more-more]");
    const lessLabel = wrap.querySelector("[data-om-read-more-less]");

    if (!content || !toggle) return;

    content.classList.toggle("om__description--collapsed", !expanded);
    toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
    toggle.classList.toggle("om__read-more--expanded", expanded);

    if (moreLabel) moreLabel.hidden = expanded;
    if (lessLabel) lessLabel.hidden = !expanded;
  }

  _updateReadMore() {
    const wrap = this.querySelector("[data-om-read-more]");
    if (!wrap) return;

    const content = wrap.querySelector("[data-om-read-more-content]");
    const toggle = wrap.querySelector("[data-om-read-more-toggle]");
    const config = this._readConfig();

    if (!content || !toggle) return;

    if (!this._isMobile()) {
      toggle.hidden = true;
      content.classList.remove("om__description--collapsed");
      toggle.setAttribute("aria-expanded", "true");
      toggle.classList.remove("om__read-more--expanded");
      return;
    }

    const lines = Math.max(1, config.descriptionMaxLines || 4);
    const needsToggle = this._contentOverflows(content, lines);

    if (!needsToggle) {
      toggle.hidden = true;
      content.classList.remove("om__description--collapsed");
      toggle.setAttribute("aria-expanded", "true");
      toggle.classList.remove("om__read-more--expanded");
      return;
    }

    toggle.hidden = false;
    if (toggle.getAttribute("aria-expanded") !== "true") {
      this._setReadMoreExpanded(wrap, false);
    }
  }

  _initReadMore() {
    this._teardownReadMore();
    const wrap = this.querySelector("[data-om-read-more]");
    if (!wrap) return;

    const toggle = wrap.querySelector("[data-om-read-more-toggle]");
    if (!toggle) return;

    this._readMoreHandler = () => {
      const isExpanded = toggle.getAttribute("aria-expanded") === "true";
      this._setReadMoreExpanded(wrap, !isExpanded);
    };

    toggle.addEventListener("click", this._readMoreHandler);
    requestAnimationFrame(() => this._updateReadMore());
  }

  _teardownReadMore() {
    const wrap = this.querySelector("[data-om-read-more]");
    const toggle = wrap?.querySelector("[data-om-read-more-toggle]");
    if (toggle && this._readMoreHandler) {
      toggle.removeEventListener("click", this._readMoreHandler);
    }
    this._readMoreHandler = null;
  }
}

if (!customElements.get("our-mission")) {
  customElements.define("our-mission", OurMission);
}
